function apiError(payload, status) {
  const error = new Error(
    payload?.error || payload?.message || "No pudimos completar la operación.",
  );
  error.code = payload?.code || "request_failed";
  error.status = status;
  return error;
}

export function parseSseBlock(block) {
  let event = "message";
  const dataLines = [];

  for (const line of String(block || "").split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
  }
  if (!dataLines.length) return null;

  const rawData = dataLines.join("\n");
  let data = rawData;
  try {
    data = JSON.parse(rawData);
  } catch {
    // SSE also permits plain-text data.
  }
  return { event, data };
}

export async function consumeSseStream(response, onEvent) {
  if (!response.body) {
    throw new Error("El navegador no permite recibir la respuesta en tiempo real.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, "\n");

    let boundary = buffer.indexOf("\n\n");
    while (boundary >= 0) {
      const parsed = parseSseBlock(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (parsed) onEvent(parsed.event, parsed.data);
      boundary = buffer.indexOf("\n\n");
    }
    if (done) break;
  }

  const parsed = parseSseBlock(buffer);
  if (parsed) onEvent(parsed.event, parsed.data);
}

export function createJustinianoClient({
  apiUrl = "http://localhost:4000",
  appId,
  getAccessToken,
  surface = "floating_assistant",
  fetchImplementation = fetch,
}) {
  const basePath = `/api/mentis/v1/apps/${encodeURIComponent(appId)}`;

  async function headers(includeJson = true) {
    const token = getAccessToken ? await getAccessToken() : null;
    return {
      ...(includeJson ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    };
  }

  async function request(path, options = {}) {
    const response = await fetchImplementation(`${apiUrl}${basePath}${path}`, {
      ...options,
      headers: {
        ...(await headers(options.body !== undefined)),
        ...options.headers,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw apiError(payload, response.status);
    return payload.data;
  }

  return {
    getPageHelp(route) {
      return request(`/help?route=${encodeURIComponent(route || "/")}`);
    },

    listConversations() {
      return request("/conversations");
    },

    loadConversation(conversationId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}`,
      );
    },

    createConversation() {
      return request("/conversations", {
        method: "POST",
        body: JSON.stringify({
          title: "Conversación con Justiniano",
          metadata: { surface },
        }),
      });
    },

    async sendMessage(
      conversationId,
      content,
      onEvent,
      {
        signal,
        appContext,
        attachmentIds = [],
        notebookIds = [],
        notebookScope = "selected",
      } = {},
    ) {
      const response = await fetchImplementation(
        `${apiUrl}${basePath}/conversations/${encodeURIComponent(conversationId)}/messages`,
        {
          method: "POST",
          headers: await headers(true),
          body: JSON.stringify({
            content,
            ...(appContext ? { appContext } : {}),
            ...(attachmentIds.length ? { attachmentIds } : {}),
            ...(notebookIds.length ? { notebookIds } : {}),
            ...(notebookScope === "all" ? { notebookScope } : {}),
          }),
          signal,
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw apiError(payload, response.status);
      }
      await consumeSseStream(response, onEvent);
    },

    async uploadAttachment(conversationId, file) {
      const body = new FormData();
      body.append("file", file);
      const response = await fetchImplementation(
        `${apiUrl}${basePath}/conversations/${encodeURIComponent(conversationId)}/attachments`,
        {
          method: "POST",
          headers: await headers(false),
          body,
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw apiError(payload, response.status);
      return payload.data;
    },

    deleteAttachment(conversationId, attachmentId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`,
        { method: "DELETE" },
      );
    },

    confirmAction(conversationId, actionId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/confirm`,
        { method: "POST" },
      );
    },

    cancelAction(conversationId, actionId) {
      return request(
        `/conversations/${encodeURIComponent(conversationId)}/actions/${encodeURIComponent(actionId)}/cancel`,
        { method: "POST" },
      );
    },
  };
}
